import { useTheme } from '@mui/material/styles';
import Select, { 
  StylesConfig, 
  components, 
  ControlProps, 
  MenuProps, 
  OptionProps, 
  SingleValueProps, 
  PlaceholderProps,
  GroupBase
} from 'react-select';
import { Typography, Box } from '@mui/material';
import { useState, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
  [key: string]: any; // Allow additional properties
}

interface SingleSelectProps {
  options: Option[];
  value: Option | null;
  onChange: (selected: Option | null) => void;
  placeholder?: string;
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  compact?: boolean;
  customComponents?: {
    Option?: React.ComponentType<any>;
    [key: string]: React.ComponentType<any> | undefined;
  };
}

export function SingleSelect({ 
  options, 
  value, 
  onChange, 
  placeholder = 'Select option...', 
  label,
  isLoading = false,
  disabled = false,
  icon,
  compact = false,
  customComponents = {}
}: SingleSelectProps) {
  const theme = useTheme();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  const customStyles: StylesConfig<Option, false> = {
    control: (provided: any, state: ControlProps<Option, false>) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      borderColor: state.isFocused ? '#14bef0' : theme.palette.divider,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(20, 190, 240, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#14bef0',
      },
      borderRadius: '8px',
      padding: compact ? '0px' : '2px',
      minHeight: compact ? '32px' : '42px',
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      borderRadius: '8px',
      overflow: 'hidden',
      zIndex: 9999,
      position: 'absolute',
      width: '100%',
      marginTop: '4px',
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    container: (provided) => ({
      ...provided,
      position: 'relative',
      zIndex: 9500,
      '&:hover': {
        zIndex: 9500,
      },
      '&:focus-within': {
        zIndex: 9500,
      },
    }),
    option: (provided: any, state: OptionProps<Option, false>) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'rgba(20, 190, 240, 0.1)'
        : state.isFocused 
          ? 'rgba(20, 190, 240, 0.05)'
          : 'transparent',
      color: state.isSelected ? '#14bef0' : theme.palette.text.primary,
      '&:active': {
        backgroundColor: 'rgba(20, 190, 240, 0.15)',
      },
      cursor: 'pointer',
      padding: compact ? '6px 8px' : '10px 12px',
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    singleValue: (provided: any, state: SingleValueProps<Option>) => ({
      ...provided,
      color: theme.palette.text.primary,
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    placeholder: (provided: any, state: PlaceholderProps<Option>) => ({
      ...provided,
      color: theme.palette.text.secondary,
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      fontSize: compact ? '0.75rem' : '0.875rem',
      margin: compact ? '0' : '2px',
      padding: compact ? '0' : '2px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: compact ? '32px' : 'auto',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: compact ? '4px' : '8px',
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      padding: compact ? '4px' : '8px',
    }),
  };

  const NoOptionsMessage = (props: any) => (
    <components.NoOptionsMessage {...props}>
      <Typography variant="body2" color="text.secondary" sx={{ 
        fontSize: compact ? '0.75rem' : '0.875rem' 
      }}>
        No options available
      </Typography>
    </components.NoOptionsMessage>
  );

  const LoadingMessage = (props: any) => (
    <components.LoadingMessage {...props}>
      <Typography variant="body2" color="text.secondary" sx={{ 
        fontSize: compact ? '0.75rem' : '0.875rem' 
      }}>
        Loading...
      </Typography>
    </components.LoadingMessage>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: compact ? 0.5 : 1, 
            fontWeight: 500, 
            fontSize: compact ? '0.75rem' : '0.875rem' 
          }}
        >
          {label}
        </Typography>
      )}
      <Select<Option, false>
        options={options}
        value={value}
        onChange={(selected) => onChange(selected)}
        styles={customStyles}
        placeholder={placeholder}
        isLoading={isLoading}
        isDisabled={disabled}
        components={{
          NoOptionsMessage,
          LoadingMessage,
          ...customComponents
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        isClearable
      />
    </Box>
  );
} 